import { useMemo } from "react";
import { Share2 } from "lucide-react";

const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  viber: "Viber",
  facebook: "Facebook",
  copy_link: "Copy link",
  direct: "Direct",
};

/**
 * Compact horizontal bar breakdown. Navy bar = invited (100%),
 * inner green segment = verified portion. Purely CSS — no chart lib.
 */
export function ChannelBreakdownBars({ breakdown }) {
  const rows = useMemo(() => {
    const entries = Object.entries(breakdown || {}).map(([key, v]) => ({
      key,
      label: CHANNEL_LABELS[key] || key,
      invited: (v && v.invited) || 0,
      verified: (v && v.verified) || 0,
    }));
    const max = entries.reduce((m, r) => Math.max(m, r.invited), 0) || 1;
    // Sort by invited desc, but keep 'direct' visible even at 0.
    return entries
      .sort((a, b) => b.invited - a.invited)
      .map((r) => ({ ...r, max }));
  }, [breakdown]);

  return (
    <div
      className="rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card"
      data-testid="admin-channel-breakdown"
    >
      <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-green/25 bg-brand-green/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-green">
        <Share2 className="h-3 w-3" />
        Referral channels
      </div>
      <h3 className="mt-3 text-xl font-bold tracking-tight text-gray-900">
        Where sign-ups come from
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        Network-wide invited vs verified brokers by attributed source.
      </p>

      <ul className="mt-6 space-y-4">
        {rows.map((r) => {
          const invitedPct = (r.invited / r.max) * 100;
          // verified portion measured against invited so it's always <= bar
          const verifiedPct =
            r.invited > 0 ? (r.verified / r.invited) * 100 : 0;
          return (
            <li
              key={r.key}
              className="min-w-0"
              data-testid={`channel-row-${r.key}`}
            >
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate font-medium text-gray-800">
                  {r.label}
                </span>
                <span className="flex-none text-xs tabular-nums text-gray-500">
                  <span
                    className="font-semibold text-brand-navy"
                    data-testid={`channel-invited-${r.key}`}
                  >
                    {r.invited}
                  </span>{" "}
                  invited ·{" "}
                  <span
                    className="font-semibold text-brand-green"
                    data-testid={`channel-verified-${r.key}`}
                  >
                    {r.verified}
                  </span>{" "}
                  verified
                </span>
              </div>
              <div
                className="relative mt-2 h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
                aria-label={`${r.label}: ${r.invited} invited, ${r.verified} verified`}
              >
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-brand-navy transition-[width] duration-500"
                  style={{ width: `${invitedPct}%` }}
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-brand-green transition-[width] duration-500"
                    style={{ width: `${verifiedPct}%` }}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
